// src/pos-app/shell/DayClosedScreen.js
//
// Idle placeholder rendered by station shells (KDS, Waiter — see
// useBusinessDayGate.js) INSTEAD of their wrapped page whenever there is no
// active business day. isBusinessDay defaults to false outside opening
// hours, so this is a normal/expected state, not an error — the copy and
// visuals are deliberately calm rather than alarming, and recovery is
// automatic (the gate's poll flips dayOpen once a business day is opened
// from Cashier Sales or the manager portal) with a manual refresh as a
// fallback affordance.
//
// Presentational only — no AuroraBackground of its own. Every station shell
// already wraps its tree in one, so this renders as a glass card on the
// existing Aurora backdrop, matching the empty-state pattern CashierStation's
// Orders tab already established (icon circle + heading + body copy).
//
// Copy reuses the SAME translation keys the web FunctionsPage's business-day
// banner already uses (no_active_business_day, start_business_day_to_access)
// plus the existing refresh/refreshing keys — all already in the database,
// so no new translation keys were needed for this screen.
import React from 'react';
import { glass } from './AuroraBackground';
import { useLanguage } from '../../context/LanguageContext';

const CSS = `
.dcs-wrap{flex:1;min-height:0;display:flex;flex-direction:column;align-items:center;justify-content:center;
  gap:6px;text-align:center;padding:24px;box-sizing:border-box;}
.dcs-ic{width:52px;height:52px;border-radius:15px;margin-bottom:8px;
  display:flex;align-items:center;justify-content:center;
  background:var(--c-faint);color:var(--c-muted);border:1px solid var(--c-border);}
.dcs-h{font-size:16px;font-weight:800;letter-spacing:-.2px;color:var(--c-ink);margin:0;}
.dcs-p{font-size:13px;font-weight:600;color:var(--c-muted);margin:0;max-width:340px;line-height:1.5;}
.dcs-refresh{margin-top:14px;min-height:44px;padding:0 20px;border-radius:12px;
  border:1px solid var(--c-border);cursor:pointer;background:var(--c-surface);color:var(--c-ink);
  font-size:13px;font-weight:800;font-family:inherit;
  display:inline-flex;align-items:center;gap:8px;
  transition:transform .15s ease, background .15s ease;}
.dcs-refresh:active{transform:scale(.96);background:var(--c-faint);}
.dcs-refresh:disabled{opacity:.6;cursor:default;transform:none;}
.dcs-spin{display:inline-flex;animation:dcsSpin .8s linear infinite;}
@keyframes dcsSpin{to{transform:rotate(360deg);}}
@media (prefers-reduced-motion: reduce){.dcs-spin{animation:none !important;}}
`;

const CalendarOffIcon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M8 2v4M16 2v4M3 10h11" />
        <path d="M21 15.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h6.5" />
        <path d="m17 17 4 4m0-4-4 4" />
    </svg>
);

const RefreshIcon = (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 12a9 9 0 1 1-2.64-6.36" />
        <path d="M21 3v6h-6" />
    </svg>
);

export default function DayClosedScreen({ onRefresh, loading }) {
    const { t } = useLanguage();

    return (
        <div className="dcs-wrap" style={glass()} role="status">
            <style>{CSS}</style>
            <div className="dcs-ic" aria-hidden="true">{CalendarOffIcon}</div>
            <h2 className="dcs-h">{t('no_active_business_day')}</h2>
            <p className="dcs-p">{t('start_business_day_to_access')}</p>
            {onRefresh ? (
                <button type="button" className="dcs-refresh" onClick={onRefresh} disabled={!!loading}>
                    <span className={loading ? 'dcs-spin' : undefined}>{RefreshIcon}</span>
                    {loading ? t('refreshing') : t('refresh')}
                </button>
            ) : null}
        </div>
    );
}
